package uz.mint.frontend.seo

import kotlinx.browser.document
import org.w3c.dom.asList
import uz.mint.frontend.SiteConfig
import uz.mint.frontend.i18n.Locale

/**
 * The pages that exist in every language and are worth ranking.
 *
 * Being an enum rather than a raw path is what keeps the alternates honest: hreflang has to name
 * *the same page* in each language, and the only way to be sure of that is to derive all three
 * addresses from one place. A page passing its own string would eventually point `/uz/plans` at
 * the Russian home page and nothing would complain.
 */
enum class IndexablePage {
    HOME,
    PLANS,
    FAQ,
    OFFER,
    PRIVACY,
    ;

    fun pathFor(locale: Locale): String = when (this) {
        HOME -> locale.homePath
        PLANS -> locale.plansPath
        FAQ -> locale.faqPath
        OFFER -> locale.offerPath
        PRIVACY -> locale.privacyPath
    }
}

/** Marks the head elements this file owns, so navigating replaces them instead of piling them up. */
private const val MANAGED_ATTR = "data-mint-seo"

/**
 * Writes canonical, hreflang and robots into the document head.
 *
 * Called from the page layout for the same reason the title is: static export renders in a
 * headless browser and snapshots the resulting DOM, so anything written here lands in the HTML a
 * crawler receives.
 *
 * A [page] of `null` means "not marketing surface" — login, profile and My eSIMs. Those export a
 * skeleton that asks the backend who is looking, so there is nothing to rank and three thin
 * near-duplicates per language to lose ranking over. They get `noindex, follow`: keep out of the
 * index, still walk the links.
 */
fun applySeo(page: IndexablePage?, locale: Locale) {
    val head = document.head ?: return

    // Clear ours first. The site is a single-page app once loaded, so a page that appended
    // without clearing would accumulate every previously-visited page's alternates.
    head.querySelectorAll("[$MANAGED_ATTR]").asList().forEach { (it as? org.w3c.dom.Element)?.remove() }

    if (page == null) {
        val meta = document.createElement("meta")
        meta.setAttribute("name", "robots")
        meta.setAttribute("content", "noindex, follow")
        meta.setAttribute(MANAGED_ATTR, "")
        head.appendChild(meta)
        return
    }

    fun url(target: Locale) = SiteConfig.CANONICAL_ORIGIN + page.pathFor(target)

    fun link(rel: String, href: String, hreflang: String? = null) {
        val el = document.createElement("link")
        el.setAttribute("rel", rel)
        el.setAttribute("href", href)
        hreflang?.let { el.setAttribute("hreflang", it) }
        el.setAttribute(MANAGED_ATTR, "")
        head.appendChild(el)
    }

    // Absolute, and pointing at the real domain rather than wherever this copy is being served
    // from — during export that is a headless browser on localhost, and a canonical naming
    // localhost would tell Google the page's true address is one it cannot reach.
    link("canonical", url(locale))

    // Every language lists every language, itself included: that is what the spec asks for, and
    // a one-way reference is ignored.
    Locale.entries.forEach { alternate ->
        link("alternate", url(alternate), alternate.code)
    }

    // Which one to serve someone whose language we do not publish. Russian owns the site root.
    link("alternate", url(Locale.DEFAULT), "x-default")
}
